import {
  MAX_ATTACHMENT_BYTES,
  MAX_COMMENT_LENGTH,
  type CollaborationSummary,
  type TaskAttachment,
  type TaskComment,
} from '@bigint-project/cloud-contracts';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from 'react';

import type { TaskCollaborationPort } from '../../platform/ports';

interface TaskCollaborationPanelProps {
  readonly taskId: string;
  readonly collaboration: TaskCollaborationPort;
}

function formatTime(iso: string): string {
  const time = new Date(iso);
  return Number.isNaN(time.getTime()) ? iso : time.toLocaleString('zh-TW', { hour12: false });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function errorText(reason: unknown): string {
  return reason instanceof Error ? reason.message : '協作操作失敗。';
}

/** 看板／格線上顯示留言與附件數；兩者都是零就不佔位。 */
export function CollaborationBadge({
  comments,
  attachments,
}: {
  readonly comments: number;
  readonly attachments: number;
}): ReactElement | null {
  if (comments === 0 && attachments === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
      {comments > 0 ? <span aria-label={`留言 ${comments} 則`}>💬{comments}</span> : null}
      {attachments > 0 ? <span aria-label={`附件 ${attachments} 個`}>📎{attachments}</span> : null}
    </span>
  );
}

/** 一次取回整個專案的留言／附件計數，供列表類視圖顯示 CollaborationBadge。 */
export function useCollaborationSummary(collaboration: TaskCollaborationPort | null): {
  readonly summary: CollaborationSummary | null;
  readonly refresh: () => void;
} {
  const [summary, setSummary] = useState<CollaborationSummary | null>(null);
  const [token, setToken] = useState(0);
  const refresh = useCallback(() => setToken((value) => value + 1), []);

  useEffect(() => {
    if (collaboration === null) {
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    void collaboration.summary().then(
      (next) => {
        if (!cancelled) setSummary(next);
      },
      () => {
        if (!cancelled) setSummary(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [collaboration, token]);

  return { summary, refresh };
}

export function TaskCollaborationPanel({
  taskId,
  collaboration,
}: TaskCollaborationPanelProps): ReactElement {
  const [comments, setComments] = useState<readonly TaskComment[]>([]);
  const [attachments, setAttachments] = useState<readonly TaskAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  // 每次載入或變更任務都換一個世代編號；換任務或 unmount 之後回來的結果一律丟棄，
  // 否則上一個任務的留言會被套到新任務上。
  const generationRef = useRef(0);

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const isCurrent = (): boolean => generationRef.current === generation;
    // 掛載處以 taskId 當 key，換任務是重掛；這裡的清空只為了 collaboration 本身換掉的情況。
    setComments([]);
    setAttachments([]);
    setLoading(true);
    setError(null);
    void Promise.all([collaboration.listComments(taskId), collaboration.listAttachments(taskId)]).then(
      ([nextComments, nextAttachments]) => {
        if (!isCurrent()) return;
        setComments(nextComments);
        setAttachments(nextAttachments);
      },
      (reason: unknown) => {
        if (isCurrent()) setError(errorText(reason));
      },
    ).finally(() => {
      if (isCurrent()) setLoading(false);
    });
    return () => {
      // 讓進行中的請求與寫入操作的結果都過期。
      generationRef.current += 1;
    };
  }, [collaboration, taskId]);

  const canDelete = (authorUserId: string): boolean =>
    collaboration.canWrite && (collaboration.isOwner || authorUserId === collaboration.currentUserId);

  const submitComment = async (): Promise<void> => {
    const body = draft.trim();
    if (body.length === 0 || busy) return;
    const generation = generationRef.current;
    setBusy(true);
    setError(null);
    try {
      const created = await collaboration.addComment(taskId, body);
      if (generationRef.current !== generation) return;
      setComments((current) => [...current, created]);
      setDraft('');
    } catch (reason) {
      if (generationRef.current === generation) setError(errorText(reason));
    } finally {
      if (generationRef.current === generation) setBusy(false);
    }
  };

  const removeComment = async (commentId: string): Promise<void> => {
    const generation = generationRef.current;
    setBusy(true);
    setError(null);
    try {
      await collaboration.deleteComment(taskId, commentId);
      if (generationRef.current !== generation) return;
      setComments((current) => current.filter((comment) => comment.id !== commentId));
    } catch (reason) {
      if (generationRef.current === generation) setError(errorText(reason));
    } finally {
      if (generationRef.current === generation) setBusy(false);
    }
  };

  const uploadFile = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError(`附件不得超過 ${formatSize(MAX_ATTACHMENT_BYTES)}。`);
      return;
    }
    const generation = generationRef.current;
    setBusy(true);
    setError(null);
    try {
      const created = await collaboration.uploadAttachment(taskId, file);
      if (generationRef.current !== generation) return;
      setAttachments((current) => [...current, created]);
    } catch (reason) {
      if (generationRef.current === generation) setError(errorText(reason));
    } finally {
      if (generationRef.current === generation) setBusy(false);
    }
  };

  const removeAttachment = async (attachmentId: string): Promise<void> => {
    const generation = generationRef.current;
    setBusy(true);
    setError(null);
    try {
      await collaboration.deleteAttachment(taskId, attachmentId);
      if (generationRef.current !== generation) return;
      setAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
    } catch (reason) {
      if (generationRef.current === generation) setError(errorText(reason));
    } finally {
      if (generationRef.current === generation) setBusy(false);
    }
  };

  const onDraftKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void submitComment();
    }
  };

  return (
    <section aria-label="任務協作" className="grid gap-4 text-sm">
      {error !== null ? <p role="alert" className="text-xs text-danger">{error}</p> : null}
      {loading ? <p role="status" className="text-xs text-fg-muted">正在載入協作內容…</p> : null}

      <div className="grid gap-2">
        <h3 className="text-sm font-semibold">留言</h3>
        {!loading && comments.length === 0 ? <p className="text-xs text-fg-muted">還沒有留言。</p> : null}
        <ul className="grid gap-2">
          {comments.map((comment) => (
            <li key={comment.id} className="rounded border border-line bg-raised p-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{comment.authorDisplayName}</span>
                <span className="text-xs text-fg-subtle">{formatTime(comment.createdAt)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap">{comment.body}</p>
              {canDelete(comment.authorUserId) ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void removeComment(comment.id)}
                  className="mt-1 text-xs text-danger disabled:opacity-50"
                >
                  刪除留言
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {collaboration.canWrite ? (
          <div className="grid gap-1">
            <label className="grid gap-1 text-xs text-fg-muted">
              新增留言（⌘/Ctrl + Enter 送出）
              <textarea
                value={draft}
                maxLength={MAX_COMMENT_LENGTH}
                rows={3}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={onDraftKeyDown}
                className="resize-y rounded border border-line bg-surface px-2 py-1.5 text-sm text-fg"
              />
            </label>
            <div className="flex justify-end">
              <button
                type="button"
                disabled={busy || draft.trim().length === 0}
                onClick={() => void submitComment()}
                className="rounded bg-accent px-3 py-1.5 text-xs text-white disabled:opacity-50"
              >
                送出留言
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="grid gap-2">
        <h3 className="text-sm font-semibold">附件</h3>
        {!loading && attachments.length === 0 ? <p className="text-xs text-fg-muted">還沒有附件。</p> : null}
        <ul className="grid gap-2">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="flex flex-wrap items-baseline gap-2 rounded border border-line bg-raised p-2"
            >
              <a
                href={collaboration.attachmentDownloadUrl(taskId, attachment.id)}
                className="font-medium text-accent underline"
              >
                {attachment.fileName}
              </a>
              <span className="text-xs text-fg-subtle">{formatSize(attachment.sizeBytes)}</span>
              <span className="text-xs text-fg-muted">{attachment.uploaderDisplayName}</span>
              {canDelete(attachment.uploaderUserId) ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void removeAttachment(attachment.id)}
                  className="text-xs text-danger disabled:opacity-50"
                >
                  刪除附件
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {collaboration.canWrite ? (
          <label className="grid gap-1 text-xs text-fg-muted">
            上傳附件（單檔上限 {formatSize(MAX_ATTACHMENT_BYTES)}）
            <input
              type="file"
              disabled={busy}
              onChange={(event) => void uploadFile(event)}
              className="text-sm text-fg"
            />
          </label>
        ) : null}
      </div>
    </section>
  );
}
